/**
 * SECURE TOKEN STORAGE SERVICE
 * 
 * SECURITY IMPLEMENTATION:
 * - Access tokens stored in memory ONLY (not localStorage)
 * - Refresh tokens handled via HttpOnly cookies (server-side)
 * - Automatic token refresh with rotation
 * - XSS attack prevention
 * - Token expiration handling
 */

import { secureLogger } from '@/utils/secureLogger';

export interface TokenData {
  accessToken: string;
  expiresAt: number;
  isExpired: () => boolean;
}

class SecureTokenStorage {
  private accessToken: string | null = null;
  private tokenExpiresAt: number | null = null;
  private refreshPromise: Promise<string | null> | null = null;
  private lastRefreshAt: number = 0;

  /**
   * SECURITY: Store access token in memory only
   * Never in localStorage or sessionStorage to prevent XSS attacks
   */
  public setAccessToken(token: string, expiresIn: number): void {
    this.accessToken = token;
    // Calculate expiration time (subtract 30 seconds for safety margin)
    this.tokenExpiresAt = Date.now() + (expiresIn * 1000) - 30000;
    
    // Token stored - no logging for security
  }

  /**
   * Get current access token if valid, otherwise attempt refresh
   */
  public async getValidAccessToken(): Promise<string | null> {
    // If no token, attempt refresh using HttpOnly cookie
    if (!this.accessToken || !this.tokenExpiresAt) {
      return await this.refreshAccessToken();
    }

    // If token is still valid, return it
    if (Date.now() < this.tokenExpiresAt) {
      return this.accessToken;
    }

    // Token expired, attempt refresh
    secureLogger.debug('🔄 Access token expired, attempting refresh...');
    return await this.refreshAccessToken();
  }

  /**
   * Check if current token is expired
   */
  public isTokenExpired(): boolean {
    if (!this.tokenExpiresAt) return true;
    return Date.now() >= this.tokenExpiresAt;
  }

  /**
   * Get token expiration time
   */
  public getTokenExpiration(): Date | null {
    if (!this.tokenExpiresAt) return null;
    return new Date(this.tokenExpiresAt);
  }

  /**
   * SECURITY: Refresh access token using HttpOnly cookie
   * Only one refresh attempt at a time to prevent race conditions
   */
  private async refreshAccessToken(): Promise<string | null> {
    // Prevent concurrent refresh attempts
    if (this.refreshPromise) {
      return await this.refreshPromise;
    }

    this.refreshPromise = this._performTokenRefresh();
    
    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh API call
   */
  private async _performTokenRefresh(): Promise<string | null> {
    try {
      // Don't attempt refresh if we're already on the login page
      if (typeof window !== 'undefined' && window.location.pathname === '/login') {
        secureLogger.debug('🔄 Skipping token refresh - already on login page');
        return null;
      }

      // Normalize base so we do not end up with /api/v1/api/v1
      const rawBase = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const apiOrigin = rawBase.replace(/\/api\/v1\/?$/, '');

      // More aggressive throttle to avoid hammering the endpoint on mount loops
      const now = Date.now();
      const timeSinceLastRefresh = now - this.lastRefreshAt;
      if (timeSinceLastRefresh < 2000) { // Increased from 1000ms to 2000ms
        secureLogger.debug(`🔄 Throttling refresh attempt (${timeSinceLastRefresh}ms since last)`);
        await new Promise(r => setTimeout(r, 2000 - timeSinceLastRefresh));
      }
      this.lastRefreshAt = Date.now();

      const response = await fetch(`${apiOrigin}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // SECURITY: Include HttpOnly cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.access_token) {
          // Store new access token
          this.setAccessToken(data.data.access_token, data.data.expires_in);
          secureLogger.debug('✅ Token refreshed successfully');
          return data.data.access_token;
        }
      }

      // Refresh failed
      secureLogger.warn('❌ Token refresh failed');
      this.clearTokens();
      
      // Redirect to login if refresh fails
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      
      return null;
    } catch (error) {
      secureLogger.error('❌ Token refresh error:', error);
      this.clearTokens();
      return null;
    }
  }

  /**
   * SECURITY: Clear all tokens from memory
   */
  public clearTokens(): void {
    this.accessToken = null;
    this.tokenExpiresAt = null;
    secureLogger.debug('🧹 All tokens cleared from memory');
  }

  /**
   * Check if user is authenticated (has valid token)
   */
  public async isAuthenticated(): Promise<boolean> {
    const token = await this.getValidAccessToken();
    return token !== null;
  }

  /**
   * SECURITY: Logout - clear tokens and revoke refresh token
   */
  public async logout(): Promise<void> {
    try {
      // Call logout endpoint to revoke refresh token
      await fetch('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'include', // Include HttpOnly cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        secureLogger.warn('Logout request failed:', error);
      }
    }

    // Clear in-memory tokens
    this.clearTokens();
    
    if (import.meta.env.DEV) {
      secureLogger.debug('🚪 User logged out securely');
    }
  }

  /**
   * Get current access token without validation (for immediate use)
   * WARNING: May return expired token - use getValidAccessToken() for API calls
   */
  public getCurrentToken(): string | null {
    return this.accessToken;
  }
}

// Export singleton instance
export const secureTokenStorage = new SecureTokenStorage();
export default secureTokenStorage;